// ── LeadForm ──
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, FInput, FInputNum, FSelect, Field, Modal, ReadOnlyForm } from "../../../shared/ui";
import { useForm } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { NotesPanel } from "../../../shared/NotesPanel";
import { DuplicateCheck } from "../components/DuplicateCheck";
import { EmailActivityPanel } from "../../../shared/EmailActivityPanel";

const STATUS_OPTS = ["New", "Working", "Qualified", "Unqualified"].map(s => ({ value: s, label: s }));

export const LeadForm = ({ initial, referralsourceid, readOnly }) => {
  const { api, role, currentUser, modal, setModal, closeModal, saving, error, saveRec } = useApp();
  const blank = {
    companyname: "", lead_source: "", owner: "", status: "New",
    firstname: "", lastname: "", title: "", email: "", phone: "", mobilephone: "",
    website: "", street: "", city: "", state: "", zipcode: "", country: "",
    requested_amount: "", referralsourceid: referralsourceid || "", notes: "",
  };
  const [f, set, setF] = useForm(initial || blank);
  const [owners, setOwners] = useState([]);
  const [refSources, setRefSources] = useState([]);

  useEffect(() => {
    api.get("getUsers").then(r => setOwners(Array.isArray(r) ? r : [])).catch(() => {});
    api.get("getReferralSources").then(r => setRefSources(Array.isArray(r) ? r : [])).catch(() => {});
    // Owner defaults to the creating user (server enforces this too).
    if (!initial?.id && currentUser?.id) set("owner")(currentUser.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ownerOpts = [{ value: "", label: "— Select —" }, ...owners.map(u => ({ value: u.id, label: u.FullName }))];
  // Every lead — new or existing — must say who the company is and where it
  // came from: Company Name and Lead Source. (Referral Source is optional.)
  // saveLead enforces the same rule server-side.
  const refLabel = r => {
    const person = (r.full_name || [r.firstname, r.lastname].filter(Boolean).join(" ")).trim();
    const co = (r.company_name || "").trim();
    return co && person ? `${co} — ${person}` : (co || person || "(unnamed source)");
  };
  const refOpts = [{ value: "", label: "— None —" },
    ...refSources.map(r => ({ value: r.id, label: refLabel(r) })).sort((a, b) => a.label.localeCompare(b.label))];
  const blankStr = v => !String(v ?? "").trim();

  const missing = [];
  if (blankStr(f.companyname)) missing.push("Company Name");
  if (blankStr(f.lead_source)) missing.push("Lead Source");
  const validationError = missing.length ? "Required: " + missing.join(", ") : null;
  const req = !readOnly;   // red asterisk whenever the form is editable

  const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;

  return (
    <Modal wide title={readOnly ? "View Lead" : initial ? "Edit Lead" : "Add Lead"} onClose={closeModal}
      onEdit={readOnly && role.canEdit ? () => { closeModal(); setModal({ type:"lead-edit", rec:initial, onDone:modal.onDone }); } : null}>
      <ReadOnlyForm readOnly={readOnly}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px" }}>
          <ST title="Lead" />
          {/* Clearance check. A restricted BDO can't browse other reps' books,
              so this is how they find out a company is already taken — before
              they've spent twenty minutes typing up a lead someone else owns. */}
          {!readOnly && !initial?.id && (
            <div style={{ gridColumn:"1/-1" }}>
              <DuplicateCheck company={f.companyname} email={f.email} />
            </div>
          )}
          <Field label="Company Name" required={req}><FInput value={f.companyname} onChange={set("companyname")} /></Field>
          <Field label="Status"><FSelect value={f.status} onChange={set("status")} options={STATUS_OPTS} /></Field>
          <Field label="Owner"><FSelect value={f.owner} onChange={set("owner")} options={ownerOpts} disabled={!role.isAdmin} /></Field>
          <Field label="Referral Source"><FSelect value={f.referralsourceid} onChange={set("referralsourceid")} options={refOpts} /></Field>
          <Field label="Lead Source" required={req}><FInput value={f.lead_source} onChange={set("lead_source")} /></Field>
          <div />

          <ST title="Primary Contact" />
          {!readOnly && (
            <div style={{ gridColumn:"1/-1", fontSize:11, color:"var(--color-text-secondary)", marginTop:-4, marginBottom:8 }}>
              First Name, Last Name and Email aren't needed to save, but are required before the lead can be converted.
            </div>
          )}
          <Field label="First Name"><FInput value={f.firstname} onChange={set("firstname")} /></Field>
          <Field label="Last Name"><FInput value={f.lastname} onChange={set("lastname")} /></Field>
          <Field label="Title"><FInput value={f.title} onChange={set("title")} /></Field>
          <Field label="Email"><FInput value={f.email} onChange={set("email")} /></Field>
          <Field label="Phone"><FInput value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Mobile"><FInput value={f.mobilephone} onChange={set("mobilephone")} /></Field>

          <ST title="Company Detail" />
          <Field label="Website"><FInput value={f.website} onChange={set("website")} /></Field>
          <Field label="Street"><FInput value={f.street} onChange={set("street")} /></Field>
          <Field label="City"><FInput value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><FInput value={f.state} onChange={set("state")} /></Field>
          <Field label="Zip"><FInput value={f.zipcode} onChange={set("zipcode")} /></Field>
          <Field label="Country"><FInput value={f.country} onChange={set("country")} /></Field>

          <ST title="Deal" />
          {/* The column is still requested_amount in the database; this is the
              label the business uses. It opens the opportunity's RLOC Amount on
              conversion, which is what Commitment is computed from. */}
          <Field label="Commitment Amount"><FInputNum value={f.requested_amount} onChange={set("requested_amount")} /></Field>
          <div />
          <div style={{ gridColumn:"1/-1" }}>
            <Field label="Notes"><FInput value={f.notes || ""} onChange={set("notes")} /></Field>
          </div>
        </div>
      </ReadOnlyForm>

      {initial?.id && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>Notes</div>
          <NotesPanel entitytype="Lead" entityid={initial.id} />
        </div>
      )}

      {initial?.id && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:10, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight }}>Email</div>
          <EmailActivityPanel entitytype="Lead" entityid={initial.id} />
        </div>
      )}

      {!readOnly && validationError && <div style={{ color:"#993C1D", fontSize:12, marginTop:10 }}>{validationError}</div>}
      {!readOnly && error && <div style={{ color:"#993C1D", fontSize:12, marginTop:8 }}>{error}</div>}

      <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:12 }}>
        <Btn onClick={closeModal}>{readOnly ? "Close" : "Cancel"}</Btn>
        {!readOnly && <Btn disabled={saving || !!validationError}
          onClick={() => saveRec("saveLead", { ...f }, initial ? "Edited" : "Added", "Leads", (initial ? "Updated" : "Added") + " lead", modal.onDone)}
          variant="primary">{saving ? "Saving..." : "Save"}</Btn>}
      </div>
    </Modal>
  );
};
